import { push } from 'connected-react-router';
import React, { useContext } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { Url } from 'src/api';
import Client from 'src/models/Client';
import { SelectItem } from 'src/models/common/SelectItem';
import State from 'src/models/State';
import { AppContext } from 'src/providers/AppContextProvider';
import { IAppContextState } from 'src/reducers/AppContext';
import useFetch from 'use-http';
import Page from '../common/Page';
import Panel from '../common/Panel';
import AddressData from '../controls/forms/composite/AddressData';
import { FormContainer, FormError, FormRow, TextInput } from '../controls/forms/reactHookForm';
import Button from '../controls/forms/redesign/FormButton';

const CreateClient: React.FC = () => {
    const appContext = useContext<IAppContextState>(AppContext);
    const { get, post, response, error, loading } = useFetch();

    const [isIntegrated, setIsIntegrated] = React.useState(true);
    const [searchRequestSent, setSearchRequestSent] = React.useState(false);
    const [client, setClient] = React.useState<Client>(undefined);
    const [states, setStates] = React.useState<Array<SelectItem>>(undefined);
    const dispatch = useDispatch();
    const [apiError, setApiError] = React.useState<string>(undefined);

    const nonIntegratedMethods = useForm();
    const integratedMethods = useForm();

    const loadStates = async () => {
        const result = await get('v1.0/states?$skip=0&$top=100');

        if (response.ok) {
            setStates(result.items.map((state: State) => new SelectItem(state.id, state.name)));
        }
    };

    React.useEffect(() => {
        loadStates();
    }, []);

    React.useEffect(() => {
        setSearchRequestSent(false);
        setClient(undefined);
    }, [isIntegrated]);

    const onSubmitNonIntegrated = async (c: Client) => {
        if (c.phoneNumber.number) {
            c.phoneNumber.isPrimary = true;
            c.phoneNumber.typeId = 2; // Need to work into a client-side enum
        }

        const result = await post(Url.client.clients(), c);

        if (response.ok) {
            dispatch(push(`/clients/${result.id}`));
        } else {
            setApiError(result.message);
        }
    };

    const onSearchIntegrated = async () => {
        setSearchRequestSent(true);
        setClient(undefined);
        const result = await get(`${Url.client.client(appContext, parseInt(integratedMethods.getValues('clientNumber'), 10))}/integrated`);

        if (response.ok) {
            setClient(result);
        } else {
            setApiError(result);
        }
    };

    const onSubmitIntegrated = async () => {
        setApiError(undefined);
        const c = new Client();
        c.clientNumber = client.clientNumber;

        const result = await post(Url.client.clients(), c);

        if (response.ok) {
            dispatch(push(`/clients/${result.id}`));
        } else {
            setApiError(result.message);
        }
    };

    const navigate = (clientNumber: number) => {
        dispatch(push(`/clients/${clientNumber}`));
    };

    const clientPanel = () => {
        let content: React.ReactElement = undefined;

        if (!searchRequestSent) {
            content = <div />;
        }
        if (loading) {
            return (
                <div>
                    <span style={{ display: 'inline-block', marginRight: '.5rem' }}>
                        <i className="fas fa-spinner fa-pulse" color="#6396C1" />
                    </span>
                    Loading
                </div>
            );
        }

        if (!client && searchRequestSent) {
            content = (
                <>
                    <div style={{ fontWeight: 'bold' }}>Search Result</div>
                    <div style={{ padding: '1rem', border: '1px solid #ccc', background: '#f9f9f9' }}>
                        <div>Client not found</div>
                    </div>
                </>
            );
        }

        if (client && client.id !== 0) {
            content = (
                <>
                    <div style={{ fontWeight: 'bold' }}>Search Result</div>
                    <div style={{ padding: '1rem', border: '1px solid #ccc', background: '#f9f9f9' }}>
                        <span>
                            <Button
                                style={{ margin: '0', padding: '0' }}
                                buttonStyle="link"
                                text={client.name}
                                onClick={() => navigate(client.clientNumber)}
                            />{' '}
                            already exists.
                        </span>
                    </div>
                </>
            );
        }

        if (client && client.id === 0) {
            content = (
                <>
                    <div style={{ fontWeight: 'bold' }}>Search Result</div>
                    <div style={{ padding: '1rem', border: '1px solid #ccc', background: '#f9f9f9' }}>
                        <div>{client.name}</div>
                        {client.address?.addressLine1 && <div>{client.address.addressLine1}</div>}
                        {client.address?.addressLine2 && <div>{client.address.addressLine2}</div>}
                        {client.address?.city}, {client.address?.state?.abbreviation}&nbsp;&nbsp;{client.address?.zipCode?.substring(0, 5)}
                    </div>
                </>
            );
        }

        return content;
    };

    const stopSubmitOnEnter = (e: React.KeyboardEvent<HTMLFormElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
        }
    };

    const handleSearchInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            onSearchIntegrated();
        }
    };

    return (
        <Page>
            <Page.Section>
                <Panel panelTitle="Create Client">
                    {isIntegrated && (
                        <div style={{ padding: '1.5rem', paddingTop: 0, marginTop: 0 }}>
                            <FormError error={apiError} message="Unexpected error loading Client." />
                            <div>
                                <FormProvider {...integratedMethods}>
                                    <form onSubmit={integratedMethods.handleSubmit(onSubmitIntegrated)} onKeyDown={(e) => stopSubmitOnEnter(e)}>
                                        <FormContainer>
                                            <FormRow layout={[2, 1, '5_']} style={{ marginTop: 0 }}>
                                                <TextInput
                                                    isRequired={true}
                                                    label="CALMS Client Number"
                                                    name="clientNumber"
                                                    onKeyDown={(e) => handleSearchInput(e)}
                                                />
                                                <div style={{ height: '4rem', display: 'flex', alignItems: 'flex-end' }}>
                                                    <Button text="Search" size="small" intent="primary" onClick={onSearchIntegrated} />
                                                </div>
                                            </FormRow>
                                            <div style={{ margin: '1rem 0' }}>{clientPanel()}</div>
                                            <FormRow layout={[1, 3, '5_']}>
                                                <Button disabled={!client || client.id !== 0} type="submit" text="Create Client" intent="primary" />
                                                <Button
                                                    buttonStyle="link"
                                                    style={{ height: '3.5rem', textAlign: 'left' }}
                                                    text="Create Stand-Alone Client (No CALMS Integration)"
                                                    onClick={() => setIsIntegrated((state) => !state)}
                                                />
                                            </FormRow>
                                        </FormContainer>
                                    </form>
                                </FormProvider>
                            </div>
                        </div>
                    )}

                    {!isIntegrated && (
                        <div style={{ padding: '1.5rem', paddingTop: 0, marginTop: 0 }}>
                            <FormError error={error} message={apiError || 'Unexpected error occurred.'} />
                            <div>
                                <FormProvider {...nonIntegratedMethods}>
                                    <form onSubmit={nonIntegratedMethods.handleSubmit(onSubmitNonIntegrated)}>
                                        <FormContainer>
                                            <FormRow layout={[5, 4, 4]} style={{ marginTop: 0 }}>
                                                <TextInput isRequired={true} label="Client Name" name="name" />
                                                <TextInput label="Phone" name="phoneNumber.number" />
                                                <TextInput label="Website" name="website" />
                                            </FormRow>
                                            <AddressData namePrefix="address." states={states} />
                                            <FormRow layout={[1, 3, '5_']}>
                                                <Button type="submit" text="Create Client" intent="primary" />
                                                <Button
                                                    buttonStyle="link"
                                                    style={{ height: '3.5rem', textAlign: 'left' }}
                                                    text="Create CALMS Integrated Client"
                                                    onClick={() => setIsIntegrated((state) => !state)}
                                                />
                                            </FormRow>
                                        </FormContainer>
                                    </form>
                                </FormProvider>
                            </div>
                        </div>
                    )}
                </Panel>
            </Page.Section>
        </Page>
    );
};

export default CreateClient;



// WEBPACK FOOTER //
// ./src/components/clients/CreateClient.tsx